import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, MapPin, Star, ShoppingBag, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import { toast } from "sonner";

/**
 * Horizontal scrolling rail of product cards for home page sections
 * (Trending / New Arrivals / Best Sellers / Community Picks).
 * - Native touch scroll on mobile with snap-x
 * - Arrow buttons (desktop only) for keyboard/mouse users
 * - Each card opens product detail via /search?q=<name> for now (single source of truth)
 */
export default function HomeProductRail({
  title,
  icon: Icon,
  iconClass = "text-magenta-600",
  products = [],
  viewAllHref = "/marketplace",
  testId,
  loading = false,
}) {
  const scrollerRef = useRef(null);
  const { user } = useAuth();
  const { add: addToCart } = useCart();
  const requireAuth = useRequireAuth();

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollBy({ left: dir * Math.max(w * 0.85, 320), behavior: "smooth" });
  };

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { requireAuth(() => {}, "cart"); return; }
    if (p.seller_id === user?.id) { toast.error("This is your own product"); return; }
    addToCart(p);
    toast.success(`${p.name} added to cart`);
  };

  if (!loading && (!products || products.length === 0)) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid={testId}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-4 sm:mb-5">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-semibold text-amethyst-900 tracking-tight flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconClass}`} strokeWidth={1.8} />}
            {title}
          </h2>
          <div className="flex items-center gap-2">
            <Link to={viewAllHref} className="text-xs sm:text-sm font-semibold text-magenta-700 hover:text-magenta-800 inline-flex items-center gap-1" data-testid={`${testId}-view-all`}>
              See All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <div className="hidden md:flex items-center gap-1.5">
              <Button variant="outline" size="icon" onClick={() => scrollBy(-1)} className="w-8 h-8 rounded-full border-amethyst-200" aria-label="Scroll left" data-testid={`${testId}-prev`}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => scrollBy(1)} className="w-8 h-8 rounded-full border-amethyst-200" aria-label="Scroll right" data-testid={`${testId}-next`}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-40 sm:w-48 animate-pulse">
                  <div className="aspect-square rounded-xl bg-amethyst-100/70" />
                  <div className="h-3 w-3/4 bg-amethyst-100/70 rounded mt-2" />
                  <div className="h-3 w-1/2 bg-amethyst-100/70 rounded mt-1.5" />
                </div>
              ))
            : products.map((p) => (
                <Link
                  key={p.id}
                  to={`/search?q=${encodeURIComponent(p.name)}`}
                  className="flex-shrink-0 w-40 sm:w-48 snap-start card-premium overflow-hidden flex flex-col text-left group"
                  data-testid={`${testId}-card-${p.id}`}
                >
                  <div className="aspect-square bg-white relative overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-contain object-center p-2" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-amethyst-300">
                        <ShoppingBag className="w-10 h-10" strokeWidth={1.2} />
                      </div>
                    )}
                    {p.mrp && p.mrp > p.price && (
                      <Badge className="absolute top-2 left-2 bg-red-600 text-white text-[9px] px-1.5 py-0.5">
                        {Math.round(((p.mrp - p.price) / p.mrp) * 100)}% OFF
                      </Badge>
                    )}
                  </div>
                  <div className="p-2.5 sm:p-3 flex-1 flex flex-col">
                    <p className="text-[9px] uppercase tracking-wider text-red-600 font-semibold line-clamp-1">{p.category}</p>
                    <h3 className="font-semibold text-xs sm:text-sm text-amethyst-900 line-clamp-2 leading-tight mt-0.5">{p.name}</h3>
                    <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{p.seller_name}</p>
                    <div className="flex items-center gap-0.5 mt-1 text-[10px] text-red-600">
                      <Star className="w-3 h-3 fill-red-500 text-red-500" />
                      <span>{p.rating}</span>
                      <span className="text-gray-400">· {p.sold}</span>
                    </div>
                    <div className="mt-auto pt-2 flex items-center justify-between gap-1.5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900">₹{p.price}</span>
                        {p.mrp && p.mrp > p.price && (
                          <span className="text-[10px] text-gray-400 line-through leading-none">₹{p.mrp}</span>
                        )}
                      </div>
                      {p.seller_id !== user?.id && (
                        <button
                          type="button"
                          onClick={(e) => handleAdd(e, p)}
                          className="rounded-full text-[10px] inline-flex items-center px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm transition-all active:scale-95"
                          data-testid={`${testId}-add-${p.id}`}
                        >
                          <ShoppingCart className="w-2.5 h-2.5 mr-0.5" />Add
                        </button>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
}
